import { useMemo, useState, useEffect } from 'react';

import { IconArrowRight } from '@tabler/icons-react';

import CustomModal from '@/components/CustomModal';
import Input from '@/components/Input/Input';
import Select from '@/components/Select';
import { useFormatText } from '@/hooks/useFormatText';
import { IContentPair } from '@/pages/common/gameContent/EditGameContent';
import { getGameDetails } from '@/service/gameService';

export interface IEditGameSubmitPayload {
  quarterId: string;
  isUpdated: boolean;
  contentPairs: IContentPair[];
}

interface IEditGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: IEditGameSubmitPayload) => void;
  quarterId: string;
  /** Pre-existing content pairs from a previous edit session */
  existingPairs?: IContentPair[];
}

const EditGameModal = ({
  isOpen,
  onClose,
  onSubmit,
  quarterId,
  existingPairs,
}: IEditGameModalProps) => {
  const { formatText } = useFormatText();

  const [isLoading, setIsLoading] = useState(false);
  const [gameDetails, setGameDetails] = useState({
    title: '',
    description: '',
    quarters: 0,
    rounds: 0,
  });
  const [originalValues, setOriginalValues] = useState<string[]>([]);
  const [editableOptions, setEditableOptions] = useState<string[]>([]);
  const [currentValues, setCurrentValues] = useState<string[]>([]);

  // Fetch game details on open using quarterId
  useEffect(() => {
    if (!isOpen || !quarterId) return;

    const fetchDetails = async () => {
      setIsLoading(true);
      try {
        const res = await getGameDetails(quarterId);
        const details = res?.data ?? res;

        setGameDetails({
          title: details.title ?? '',
          description: details.description ?? '',
          quarters: details.quarters ?? 0,
          rounds: details.rounds ?? 0,
        });

        const content: string[] = details.content ?? [];
        const editable: string[] = details.editable_content ?? [];
        const merged = [...new Set([...content, ...editable])];

        setOriginalValues(content);
        setEditableOptions(merged);

        // If there are existing pairs from a previous edit, restore those
        if (existingPairs && existingPairs.length > 0) {
          setCurrentValues(existingPairs.map((p) => p.changeTo));
        } else {
          setCurrentValues([...content]);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [isOpen, quarterId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute isUpdated by comparing current vs original
  const isUpdated = useMemo(() => {
    if (originalValues.length !== currentValues.length) return true;
    return originalValues.some((val, idx) => val !== currentValues[idx]);
  }, [originalValues, currentValues]);

  const handleValueChange = (index: number, newValue: string) => {
    setCurrentValues((prev) => {
      const next = [...prev];
      next[index] = newValue;
      return next;
    });
  };

  const handleSave = () => {
    const contentPairs: IContentPair[] = originalValues.map((original, i) => ({
      original,
      changeTo: currentValues[i] ?? original,
    }));
    onSubmit({
      quarterId,
      isUpdated,
      contentPairs,
    });
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen || !quarterId) return null;

  return (
    <CustomModal
      setIsOpen={handleCancel}
      isHeader
      heading={formatText('create-lesson.edit-game.title')}
      isFooter
      position="right"
      cancelButtonProps={{
        label: formatText('common.actions.cancel'),
        variant: 'link',
        onClick: handleCancel,
      }}
      confirmButtonProps={{
        label: formatText('common.actions.save-changes'),
        variant: 'primary',
        onClick: handleSave,
        disabled: isLoading,
      }}
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-brand-default border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex flex-col gap-5 w-full">
          {/* Game Title — readonly */}
          <Input
            name="game-title"
            label={formatText('lessons.create-drawer.fields.game-title')}
            value={gameDetails.title}
            readOnly
            disabled
          />

          {/* Description — readonly */}
          <Input
            name="game-description"
            label={formatText('lessons.create-drawer.fields.game-objective')}
            value={gameDetails.description}
            readOnly
            disabled
          />

          {/* Quarter & Round — side by side */}
          <div className="flex gap-4">
            <Input
              name="quarter"
              label={formatText('create-lesson.edit-game.quarter')}
              value={String(gameDetails.quarters)}
              readOnly
              disabled
              className="flex-1"
            />
            <Input
              name="round"
              label={formatText('create-lesson.edit-game.round')}
              value={String(gameDetails.rounds)}
              readOnly
              disabled
              className="flex-1"
            />
          </div>

          {/* Content Dropdowns */}
          <div className="flex flex-col gap-3">
            <p className="text-sm font-semibold text-neutral-primary">
              {formatText('lessons.create-drawer.fields.game-content-label')}
            </p>

            <div className="flex flex-col gap-3">
              {currentValues.map((value, index) => (
                <div key={index} className="flex items-end gap-3">
                  {/* Current value — readonly */}
                  <Input
                    name={`content-original-${index}`}
                    label={formatText('lessons.create-drawer.fields.game-word')}
                    value={originalValues[index] ?? ''}
                    readOnly
                    disabled
                    className="flex-1"
                  />

                  {/* Arrow */}
                  <div className="flex items-center justify-center h-10 shrink-0 mb-0.5">
                    <IconArrowRight
                      size={18}
                      className="text-neutral-400 rtl:rotate-180"
                    />
                  </div>

                  {/* Change to — dropdown with editable content options */}
                  <Select
                    name={formatText(
                      'lessons.create-drawer.fields.game-change-to'
                    )}
                    options={editableOptions?.map((opt) => ({
                      value: opt,
                      label: opt,
                    }))}
                    selected={value}
                    onChange={(val) =>
                      handleValueChange(
                        index,
                        Array.isArray(val) ? val[0] || '' : (val as string)
                      )
                    }
                    mainClassName="flex-1"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </CustomModal>
  );
};

export default EditGameModal;
